import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2, Users, Briefcase, Wrench, HeartHandshake, Cog, ShieldCheck,
  CheckCircle2, ArrowRight, Loader2, AlertCircle,
} from "lucide-react";
import { HarborWellFormHeader } from "@/components/HarborWellFormHeader";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Human-friendly labels for backend Pydantic field paths so 422 responses
// can be rendered as inline alerts instead of raw JSON.
const FIELD_LABELS = {
  "organization.organization_name": "Organization Name",
  "organization.organization_type": "Organization Type",
  "organization.primary_service_area": "Primary Service Area",
  "organization.physical_address": "Physical Address",
  "organization.website": "Website",
  "primary_contact.first_name": "Primary Contact — First Name",
  "primary_contact.last_name": "Primary Contact — Last Name",
  "primary_contact.email": "Primary Contact — Email",
  "primary_contact.phone": "Primary Contact — Phone",
  "executive_contact.name": "Executive Contact — Name",
  "executive_contact.email": "Executive Contact — Email",
  "executive_contact.phone": "Executive Contact — Phone",
  "technical_contact.email": "Technical Contact — Email",
  "details.mission_statement": "Mission Statement",
  "details.programs_and_services": "Programs & Services",
  "details.populations_served": "Populations Served",
  "details.counties_regions_served": "Counties / Regions Served",
  "agreements.electronic_signature": "Electronic Signature",
};

function formatValidationErrors(detail) {
  if (!Array.isArray(detail)) return null;
  return detail.map((e) => {
    const path = Array.isArray(e.loc)
      ? e.loc.filter((p) => p !== "body").join(".")
      : "";
    const label = FIELD_LABELS[path] || path || "Field";
    let msg = e.msg || "invalid value";
    if (e.type === "string_too_short") {
      const min = e.ctx?.min_length;
      msg = min ? `must be at least ${min} characters` : "is too short";
    } else if (e.type === "string_too_long") {
      const max = e.ctx?.max_length;
      msg = max ? `must be ${max} characters or fewer` : "is too long";
    } else if (e.type === "missing") {
      msg = "is required";
    } else if (e.type?.startsWith("value_error") && msg.toLowerCase().startsWith("value error, ")) {
      msg = msg.slice("Value error, ".length);
    } else if (msg.toLowerCase().startsWith("value is not a valid email")) {
      msg = "must be a valid email";
    }
    return { path, label, msg };
  });
}

const ORG_TYPES = [
  { value: "nonprofit", label: "Nonprofit Organization" },
  { value: "for_profit", label: "For-Profit Business" },
  { value: "government", label: "Government / Municipal" },
  { value: "healthcare_system", label: "Healthcare System" },
  { value: "hospital", label: "Hospital" },
  { value: "clinic", label: "Clinic / FQHC" },
  { value: "faith_based", label: "Faith-Based" },
  { value: "school", label: "School / University" },
  { value: "community_group", label: "Community Group" },
  { value: "advocacy", label: "Advocacy" },
  { value: "foundation", label: "Foundation" },
  { value: "insurer", label: "Insurer / Payer" },
  { value: "other", label: "Other" },
];

const INTEGRATIONS = [
  { key: "referral_management", label: "Referral Management" },
  { key: "volunteer_management", label: "Volunteer Management" },
  { key: "care_management_collaboration", label: "Care Management Collaboration" },
  { key: "community_resource_exchange", label: "Community Resource Exchange" },
  { key: "api_integration", label: "API Integration" },
  { key: "webhooks", label: "Webhooks" },
  { key: "oauth_integration", label: "OAuth Integration" },
  { key: "reporting_analytics", label: "Reporting & Analytics" },
  { key: "other", label: "Other" },
];

const AGREEMENTS = [
  { key: "terms_of_use_accepted", label: "I have read and accept the HarborWell Connect Terms of Use." },
  { key: "privacy_policy_accepted", label: "I have read and accept the Privacy Policy." },
  { key: "data_sharing_agreement_accepted", label: "Our organization agrees to the Data Sharing Agreement." },
  { key: "security_requirements_accepted", label: "We commit to meeting HarborWell's Security Requirements." },
];

function Section({ icon: Icon, title, subtitle, children }) {
  return (
    <section className="bg-white border border-[#E5E5E0] rounded-2xl p-6 mb-6">
      <div className="flex items-start gap-3 mb-5">
        <div className="p-2 rounded-xl bg-[#F0F5FF] text-[#1E3A8A]"><Icon className="w-5 h-5" /></div>
        <div>
          <h2 className="font-semibold text-lg text-[#1E3A8A]">{title}</h2>
          {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
    </section>
  );
}

function Field({ label, required, children, full }) {
  return (
    <label className={`text-sm ${full ? "md:col-span-2" : ""}`}>
      <span className="block text-xs font-medium text-slate-600 uppercase tracking-wider mb-1">
        {label} {required && <span className="text-[#DC2626]">*</span>}
      </span>
      {children}
    </label>
  );
}

const inputCls = "w-full px-3 py-2 rounded-lg border border-[#E5E5E0] text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30";

export default function PublicPartnerApplyEnterprise() {
  const [f, setF] = useState({
    // organization
    organization_name: "", dba: "", organization_type: "nonprofit",
    website: "", primary_service_area: "", physical_address: "", mailing_address: "",
    // primary contact
    pc_first_name: "", pc_last_name: "", pc_job_title: "", pc_email: "", pc_phone: "",
    // executive contact
    exec_name: "", exec_email: "", exec_phone: "",
    // technical contact
    tech_name: "", tech_email: "", tech_phone: "",
    // details
    mission_statement: "", programs_and_services: "", populations_served: "",
    counties_regions_served: "", estimated_clients_served_annually: "",
    current_referral_process: "", existing_platform: "",
    // technical info
    expected_api_usage: "", estimated_daily_requests: "",
    expected_webhook_usage: "", integration_description: "",
    // integrations
    integration_interests: [],
    // agreements
    terms_of_use_accepted: false, privacy_policy_accepted: false,
    data_sharing_agreement_accepted: false, security_requirements_accepted: false,
    electronic_signature: "",
    other_notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [fieldErrors, setFieldErrors] = useState([]);
  const [submitError, setSubmitError] = useState(null);

  // Iter 76.5 — outreach attribution: if the URL has ``?ref=<token>``,
  // fetch the pre-fill fields the admin captured during outreach and
  // include the ref_token in the final submit payload.
  const [refToken, setRefToken] = useState(null);
  const [invitedByName, setInvitedByName] = useState(null);
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const ref = q.get("ref");
    if (!ref) return;
    setRefToken(ref);
    axios.get(`${API_URL}/api/public/partner-outreach/prefill/${ref}`)
      .then((r) => {
        if (!r.data?.found) return;
        setInvitedByName(r.data.invited_by_name || null);
        setF((prev) => ({
          ...prev,
          pc_email: prev.pc_email || r.data.email || "",
          organization_name: prev.organization_name || r.data.org_name || "",
          pc_first_name: prev.pc_first_name || (r.data.contact_name || "").split(" ")[0] || "",
          pc_last_name: prev.pc_last_name || (r.data.contact_name || "").split(" ").slice(1).join(" ") || "",
        }));
      })
      .catch(() => { /* silent — ref invalid or already applied */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upd = (k) => (e) => setF({ ...f, [k]: e.target?.value ?? e });
  const chk = (k) => (e) => setF({ ...f, [k]: e.target.checked });
  const toggle = (key) => () => {
    const set = new Set(f.integration_interests);
    set.has(key) ? set.delete(key) : set.add(key);
    setF({ ...f, integration_interests: [...set] });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setFieldErrors([]);
    setSubmitError(null);
    // Guardrails
    if (!AGREEMENTS.every((a) => f[a.key])) {
      toast.error("Please acknowledge all four agreements to submit."); return;
    }
    if (!f.electronic_signature.trim()) {
      toast.error("Please type your full name as the electronic signature."); return;
    }
    // Client-side length checks mirroring the backend Pydantic constraints,
    // so the applicant sees a friendly message before we even hit the API.
    const clientChecks = [
      ["details.mission_statement", "Mission Statement", f.mission_statement, 10],
      ["details.programs_and_services", "Programs & Services", f.programs_and_services, 5],
      ["details.populations_served", "Populations Served", f.populations_served, 2],
      ["details.counties_regions_served", "Counties / Regions Served", f.counties_regions_served, 2],
      ["organization.organization_name", "Organization Name", f.organization_name, 2],
      ["organization.primary_service_area", "Primary Service Area", f.primary_service_area, 2],
      ["organization.physical_address", "Physical Address", f.physical_address, 5],
      ["primary_contact.phone", "Primary Contact — Phone", f.pc_phone, 7],
      ["executive_contact.phone", "Executive Contact — Phone", f.exec_phone, 7],
      ["agreements.electronic_signature", "Electronic Signature", f.electronic_signature, 2],
    ];
    const localErrors = clientChecks
      .filter(([, , v, min]) => (v || "").trim().length < min)
      .map(([path, label, , min]) => ({
        path, label, msg: `must be at least ${min} characters`,
      }));
    if (localErrors.length) {
      setFieldErrors(localErrors);
      setSubmitError("Please fix the highlighted fields below.");
      toast.error("Please fix the highlighted fields below.");
      return;
    }
    setSubmitting(true);
    const payload = {
      organization: {
        organization_name: f.organization_name, dba: f.dba || null,
        organization_type: f.organization_type, website: f.website || null,
        primary_service_area: f.primary_service_area,
        physical_address: f.physical_address,
        mailing_address: f.mailing_address || null,
      },
      primary_contact: {
        first_name: f.pc_first_name, last_name: f.pc_last_name,
        job_title: f.pc_job_title || null,
        email: f.pc_email, phone: f.pc_phone,
      },
      executive_contact: {
        name: f.exec_name, email: f.exec_email, phone: f.exec_phone,
      },
      technical_contact: (f.tech_name || f.tech_email) ? {
        name: f.tech_name || null,
        email: f.tech_email || null,
        phone: f.tech_phone || null,
      } : null,
      details: {
        mission_statement: f.mission_statement,
        programs_and_services: f.programs_and_services,
        populations_served: f.populations_served,
        counties_regions_served: f.counties_regions_served,
        estimated_clients_served_annually: f.estimated_clients_served_annually
          ? parseInt(f.estimated_clients_served_annually, 10) : null,
        current_referral_process: f.current_referral_process || null,
        existing_platform: f.existing_platform || null,
      },
      integration_interests: f.integration_interests,
      technical_info: (f.integration_interests.includes("api_integration") ||
                        f.integration_interests.includes("webhooks") ||
                        f.integration_interests.includes("oauth_integration")) ? {
        expected_api_usage: f.expected_api_usage || null,
        estimated_daily_requests: f.estimated_daily_requests
          ? parseInt(f.estimated_daily_requests, 10) : null,
        expected_webhook_usage: f.expected_webhook_usage || null,
        integration_description: f.integration_description || null,
      } : null,
      agreements: {
        terms_of_use_accepted: f.terms_of_use_accepted,
        privacy_policy_accepted: f.privacy_policy_accepted,
        data_sharing_agreement_accepted: f.data_sharing_agreement_accepted,
        security_requirements_accepted: f.security_requirements_accepted,
        electronic_signature: f.electronic_signature.trim(),
        signature_date: new Date().toISOString(),
      },
      other_notes: f.other_notes || null,
      ref_token: refToken || undefined,
    };
    try {
      const r = await axios.post(`${API_URL}/api/public/partner-applications`, payload);
      setDone(r.data);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 422) {
        const parsed = formatValidationErrors(detail) || [];
        if (parsed.length) {
          setFieldErrors(parsed);
          setSubmitError("Please fix the highlighted fields below and resubmit.");
          toast.error("Please fix the highlighted fields below.");
        } else {
          setSubmitError("The application couldn't be submitted. Please double-check the fields and try again.");
          toast.error("Submission failed — please review your entries.");
        }
      } else if (typeof detail === "string") {
        setSubmitError(detail);
        toast.error(detail);
      } else {
        const fallback = err?.message || "Submission failed. Please try again.";
        setSubmitError(fallback);
        toast.error(fallback);
      }
      // Scroll the error banner into view for visibility.
      requestAnimationFrame(() => {
        document.getElementById("ae-submit-error")?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center p-6" data-testid="apply-enterprise-success">
        <div className="max-w-lg bg-white rounded-2xl border border-[#E5E5E0] p-8 text-center shadow-sm">
          <div className="w-16 h-16 rounded-full bg-[#DCFCE7] flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-[#166534]" />
          </div>
          <h1 className="font-display text-2xl font-semibold text-[#1E3A8A] mb-2">Application received</h1>
          <p className="text-slate-600 mb-4">
            Thank you for your interest in HarborWell Connect. A member of our team will review your
            application and reach out within a few business days.
          </p>
          <div className="rounded-xl bg-[#F0F5FF] p-3 text-sm mb-6">
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Reference ID</div>
            <code className="font-mono text-[#1E3A8A] font-semibold" data-testid="apply-enterprise-public-ref">
              {done.public_ref}
            </code>
            <div className="mt-3 pt-3 border-t border-[#DBEAFE]">
              <Link
                to={`/partner/status/${done.public_ref}`}
                data-testid="apply-enterprise-track-btn"
                className="inline-flex items-center gap-1 text-sm text-[#1E3A8A] font-medium hover:underline">
                Track application status <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-[#1E3A8A] hover:underline">
            Return to HarborWell.care <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <header className="bg-white border-b border-[#E5E5E0] py-4 px-6">
        <div className="max-w-6xl mx-auto">
          <HarborWellFormHeader
            eyebrow="HarborWell Connect"
            title="Partner Integration Application"
            size="md"
            testId="ae-header"
            rightSlot={
              <Link to="/partner/login" className="text-sm text-[#1E3A8A] hover:underline">
                Existing partner? Sign in →
              </Link>
            }
          />
        </div>
      </header>
      <main className="max-w-4xl mx-auto p-6" data-testid="apply-enterprise-page">
        <div className="mb-6">
          <h1 className="font-display text-3xl font-semibold text-[#1E3A8A] mb-2">
            Apply to integrate with HarborWell Connect
          </h1>
          <p className="text-slate-600 max-w-2xl">
            Complete this application to request access. A HarborWell administrator will review,
            approve, and provision your Partner Portal — you&apos;ll receive a secure activation email.
          </p>
        </div>

        {invitedByName && (
          <div
            className="max-w-3xl mx-auto mt-4 px-4 py-3 rounded-2xl bg-[#F1F5F9] border border-[#1E3A8A]/20 text-sm text-[#1E3A8A]"
            data-testid="ae-outreach-banner"
          >
            <strong>{invitedByName}</strong> invited your organization to apply — we&apos;ve
            pre-filled a few fields from that invitation. Feel free to edit anything before submitting.
          </div>
        )}

        <form onSubmit={submit}>
          {submitError && (
            <div
              id="ae-submit-error"
              data-testid="ae-submit-error"
              className="mb-6 rounded-2xl border border-[#FCA5A5] bg-[#FEF2F2] p-4 flex items-start gap-3"
              role="alert"
            >
              <AlertCircle className="w-5 h-5 text-[#DC2626] mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="font-semibold text-[#991B1B] text-sm mb-1">
                  We couldn&apos;t submit your application
                </div>
                <div className="text-sm text-[#7F1D1D] mb-2">{submitError}</div>
                {fieldErrors.length > 0 && (
                  <ul
                    className="list-disc pl-5 space-y-1 text-sm text-[#7F1D1D]"
                    data-testid="ae-submit-error-list"
                  >
                    {fieldErrors.map((fe, i) => (
                      <li key={`${fe.path}-${i}`}>
                        <span className="font-medium">{fe.label}</span> {fe.msg}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
          <Section icon={Building2} title="Organization Information" subtitle="Legal entity and physical presence.">
            <Field label="Organization Name" required>
              <input required minLength={2} maxLength={200} data-testid="ae-org-name" className={inputCls} value={f.organization_name} onChange={upd("organization_name")} />
            </Field>
            <Field label="DBA (if applicable)">
              <input data-testid="ae-dba" className={inputCls} value={f.dba} onChange={upd("dba")} />
            </Field>
            <Field label="Organization Type" required>
              <select required data-testid="ae-org-type" className={inputCls} value={f.organization_type} onChange={upd("organization_type")}>
                {ORG_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Website">
              <input data-testid="ae-website" className={inputCls} placeholder="https://" value={f.website} onChange={upd("website")} />
            </Field>
            <Field label="Primary Service Area" required full>
              <input required minLength={2} maxLength={200} data-testid="ae-service-area" className={inputCls}
                placeholder="e.g. Beaufort County, Eastern NC" value={f.primary_service_area} onChange={upd("primary_service_area")} />
            </Field>
            <Field label="Physical Address" required full>
              <input required minLength={5} maxLength={500} data-testid="ae-physical" className={inputCls} value={f.physical_address} onChange={upd("physical_address")} />
            </Field>
            <Field label="Mailing Address (if different)" full>
              <input data-testid="ae-mailing" className={inputCls} value={f.mailing_address} onChange={upd("mailing_address")} />
            </Field>
          </Section>

          <Section icon={Users} title="Primary Contact" subtitle="Day-to-day operational contact for HarborWell.">
            <Field label="First Name" required>
              <input required data-testid="ae-pc-first" className={inputCls} value={f.pc_first_name} onChange={upd("pc_first_name")} />
            </Field>
            <Field label="Last Name" required>
              <input required data-testid="ae-pc-last" className={inputCls} value={f.pc_last_name} onChange={upd("pc_last_name")} />
            </Field>
            <Field label="Job Title">
              <input data-testid="ae-pc-title" className={inputCls} value={f.pc_job_title} onChange={upd("pc_job_title")} />
            </Field>
            <Field label="Email" required>
              <input required type="email" data-testid="ae-pc-email" className={inputCls} value={f.pc_email} onChange={upd("pc_email")} />
            </Field>
            <Field label="Phone" required>
              <input required minLength={7} maxLength={40} data-testid="ae-pc-phone" className={inputCls} value={f.pc_phone} onChange={upd("pc_phone")} />
            </Field>
          </Section>

          <Section icon={Briefcase} title="Executive Contact" subtitle="Executive Director / CEO for approval decisions.">
            <Field label="Full Name" required>
              <input required minLength={1} maxLength={200} data-testid="ae-exec-name" className={inputCls} value={f.exec_name} onChange={upd("exec_name")} />
            </Field>
            <Field label="Email" required>
              <input required type="email" data-testid="ae-exec-email" className={inputCls} value={f.exec_email} onChange={upd("exec_email")} />
            </Field>
            <Field label="Phone" required>
              <input required minLength={7} maxLength={40} data-testid="ae-exec-phone" className={inputCls} value={f.exec_phone} onChange={upd("exec_phone")} />
            </Field>
          </Section>

          <Section icon={Wrench} title="Technical Contact (Optional)" subtitle="Only required if requesting API / webhook / OAuth integration.">
            <Field label="Full Name">
              <input data-testid="ae-tech-name" className={inputCls} value={f.tech_name} onChange={upd("tech_name")} />
            </Field>
            <Field label="Email">
              <input type="email" data-testid="ae-tech-email" className={inputCls} value={f.tech_email} onChange={upd("tech_email")} />
            </Field>
            <Field label="Phone">
              <input data-testid="ae-tech-phone" className={inputCls} value={f.tech_phone} onChange={upd("tech_phone")} />
            </Field>
          </Section>

          <Section icon={HeartHandshake} title="Organization Details">
            <Field label="Mission Statement" required full>
              <textarea required minLength={10} maxLength={2000} data-testid="ae-mission" className={`${inputCls} min-h-[80px]`}
                value={f.mission_statement} onChange={upd("mission_statement")}
                placeholder="Describe your organization's mission (at least 10 characters)." />
            </Field>
            <Field label="Programs & Services" required full>
              <textarea required minLength={5} maxLength={4000} data-testid="ae-programs" className={`${inputCls} min-h-[80px]`}
                value={f.programs_and_services} onChange={upd("programs_and_services")}
                placeholder="Summarize the programs and services you deliver." />
            </Field>
            <Field label="Populations Served" required>
              <input required minLength={2} maxLength={2000} data-testid="ae-populations" className={inputCls} value={f.populations_served} onChange={upd("populations_served")} />
            </Field>
            <Field label="Counties / Regions Served" required>
              <input required minLength={2} maxLength={1000} data-testid="ae-counties" className={inputCls} value={f.counties_regions_served} onChange={upd("counties_regions_served")} />
            </Field>
            <Field label="Estimated Clients Served Annually">
              <input type="number" min="0" data-testid="ae-clients" className={inputCls}
                value={f.estimated_clients_served_annually} onChange={upd("estimated_clients_served_annually")} />
            </Field>
            <Field label="Existing Care Management / Referral Platform">
              <input data-testid="ae-existing-platform" className={inputCls} value={f.existing_platform} onChange={upd("existing_platform")} />
            </Field>
            <Field label="Current Referral Process" full>
              <textarea data-testid="ae-referral-process" className={`${inputCls} min-h-[60px]`}
                value={f.current_referral_process} onChange={upd("current_referral_process")} />
            </Field>
          </Section>

          <Section icon={Cog} title="Integration Interests" subtitle="Select every module your organization is interested in.">
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-2">
              {INTEGRATIONS.map((i) => {
                const checked = f.integration_interests.includes(i.key);
                return (
                  <label key={i.key}
                    data-testid={`ae-int-${i.key}`}
                    className={`cursor-pointer border rounded-xl p-3 flex items-start gap-2 transition ${
                      checked ? "border-[#1E3A8A] bg-[#F0F5FF]" : "border-[#E5E5E0] hover:border-[#94A3B8]"
                    }`}>
                    <input type="checkbox" checked={checked} onChange={toggle(i.key)}
                      className="mt-0.5 accent-[#1E3A8A]" />
                    <span className="text-sm">{i.label}</span>
                  </label>
                );
              })}
            </div>
          </Section>

          {(f.integration_interests.includes("api_integration") ||
             f.integration_interests.includes("webhooks") ||
             f.integration_interests.includes("oauth_integration")) && (
            <Section icon={Wrench} title="Technical Information" subtitle="For API / webhook / OAuth access requests.">
              <Field label="Expected API Usage" full>
                <textarea data-testid="ae-api-usage" className={`${inputCls} min-h-[60px]`}
                  value={f.expected_api_usage} onChange={upd("expected_api_usage")} />
              </Field>
              <Field label="Estimated Daily Requests">
                <input type="number" min="0" data-testid="ae-daily-req" className={inputCls}
                  value={f.estimated_daily_requests} onChange={upd("estimated_daily_requests")} />
              </Field>
              <Field label="Expected Webhook Usage">
                <input data-testid="ae-webhook-usage" className={inputCls}
                  value={f.expected_webhook_usage} onChange={upd("expected_webhook_usage")} />
              </Field>
              <Field label="Integration Description" full>
                <textarea data-testid="ae-int-desc" className={`${inputCls} min-h-[70px]`}
                  value={f.integration_description} onChange={upd("integration_description")} />
              </Field>
            </Section>
          )}

          <Section icon={ShieldCheck} title="Compliance & Agreements"
                    subtitle="Please acknowledge each item to submit.">
            <div className="md:col-span-2 space-y-2">
              {AGREEMENTS.map((a) => (
                <label key={a.key} className="flex items-start gap-3 p-3 rounded-xl border border-[#E5E5E0] hover:bg-slate-50 cursor-pointer">
                  <input type="checkbox" data-testid={`ae-agr-${a.key}`}
                    checked={f[a.key]} onChange={chk(a.key)} className="mt-0.5 accent-[#1E3A8A]" />
                  <span className="text-sm">{a.label}</span>
                </label>
              ))}
            </div>
            <Field label="Electronic Signature (type your full legal name)" required full>
              <input required minLength={2} maxLength={200} data-testid="ae-signature" className={inputCls}
                value={f.electronic_signature} onChange={upd("electronic_signature")} placeholder="e.g. Jane Doe" />
            </Field>
            <div className="md:col-span-2 text-xs text-slate-500">
              By submitting, you confirm the information above is accurate and that you are
              authorised to represent your organization. Submitting today ({new Date().toLocaleDateString()})
              constitutes your electronic signature.
            </div>
          </Section>

          <Section icon={HeartHandshake} title="Anything else we should know?">
            <Field label="Additional Notes" full>
              <textarea data-testid="ae-notes" className={`${inputCls} min-h-[70px]`}
                value={f.other_notes} onChange={upd("other_notes")}
                placeholder="Optional — anything you'd like the HarborWell team to know." />
            </Field>
          </Section>

          <div className="flex items-center justify-end gap-3 mt-6">
            <Link to="/" className="text-sm text-slate-500 hover:underline">Cancel</Link>
            <button type="submit" disabled={submitting}
              data-testid="ae-submit-btn"
              className="inline-flex items-center gap-2 bg-[#1E3A8A] text-white px-6 py-3 rounded-full text-sm font-medium hover:opacity-90 disabled:opacity-50">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              Submit application
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}
